import { WriterInfoServiceExtension } from '@blocksuite/affine/shared/services';
import { OnEvent, Service } from '@toeverything/infra';

import { type Workspace, WorkspaceInitialized } from '../../workspace';
import type { DocImpl } from '../../workspace/impls/doc';
import type { AuthAccountInfo } from '../entities/session';
import type { WorkspaceServerService } from './workspace-server';

/**
 * This service is used to set the writer info for the blocksuite editor.
 */
@OnEvent(WorkspaceInitialized, i => i.onWorkspaceInitialized)
export class BlocksuiteWriterInfoService extends Service {
  private readonly configuredDocs = new WeakSet<DocImpl>();

  constructor(private readonly workspaceServerService: WorkspaceServerService) {
    super();
  }

  onWorkspaceInitialized(workspace: Workspace) {
    const getAccount = () =>
      this.workspaceServerService.server?.account$.value ?? null;
    const getDisplayName = (account: AuthAccountInfo) =>
      account.label?.trim() || account.email?.trim() || account.id;

    const updateAwarenessUser = (doc: DocImpl) => {
      const account = getAccount();
      if (!account) {
        return;
      }
      doc.awarenessStore.awareness.setLocalStateField('user', {
        id: account.id,
        name: getDisplayName(account),
        avatar: account.avatar,
      });
    };

    const setWriterInfo = (doc: DocImpl) => {
      updateAwarenessUser(doc);
      if (this.configuredDocs.has(doc)) {
        return;
      }
      this.configuredDocs.add(doc);
      doc.storeExtensions.push(
        WriterInfoServiceExtension({
          getWriterInfo: () => {
            const currentAccount = getAccount();
            if (!currentAccount) {
              return null;
            }
            return {
              id: currentAccount.id,
              name: getDisplayName(currentAccount),
              avatar: currentAccount.avatar,
            };
          },
        })
      );
    };

    const refreshDocs = () => {
      workspace.docCollection.docs.forEach(doc =>
        setWriterInfo(doc as DocImpl)
      );
    };

    refreshDocs();

    const subscription = workspace.docCollection.meta.docMetaAdded.subscribe(
      docId => {
        const doc = workspace.docCollection.docs.get(docId);
        if (doc) {
          setWriterInfo(doc as DocImpl);
        }
      }
    );
    const docListSubscription =
      workspace.docCollection.slots.docListUpdated.subscribe(refreshDocs);

    let accountSubscription: { unsubscribe(): void } | undefined;
    const bindAccountSubscription = () => {
      accountSubscription?.unsubscribe();
      accountSubscription =
        this.workspaceServerService.server?.account$.subscribe(refreshDocs);
      refreshDocs();
    };
    const serverSubscription = this.workspaceServerService.server$.subscribe(
      bindAccountSubscription
    );
    bindAccountSubscription();

    this.disposables.push(() => subscription.unsubscribe());
    this.disposables.push(() => docListSubscription.unsubscribe());
    this.disposables.push(() => serverSubscription.unsubscribe());
    this.disposables.push(() => accountSubscription?.unsubscribe());
  }
}
